// ────────────────────────────────────────────────────────────────────────────
// Cap-aware contribution resolver. Pure functions — no React, no I/O.
//
// Slice 2 takes the user's per-account ContributionRule list (each with a
// taxTreatment) and applies the IRS cap stack:
//   - 402(g) — combined cap on pretax + Roth 401(k) employee deferrals
//   - 415(c) — total "annual additions" (employee + employer + after-tax)
//   - IRA limit — separate ceiling on direct IRA / Roth-IRA contributions
//
// Rules execute in order (`rule.order`); the first rules of each cap class
// fill their cap, then later ones get truncated. Spillover does NOT happen
// automatically: if a pretax rule for $40k exceeds 402(g), the extra $16.5k
// is reported in `cappedByRule` for warning display, but the engine doesn't
// silently re-route into after-tax / mega-backdoor. Users add explicit rules
// for those treatments — that's the authored intent the engine respects.
// ────────────────────────────────────────────────────────────────────────────

import type {
  Compensation,
  ContributionRule,
  ContributionSchedule,
  PayMonth,
} from '../types';
import {
  annualAdditionsLimit,
  electiveDeferralLimit,
  iraContributionLimit,
} from '../tax/data';

// ────────────────────────────────────────────────────────────────────────────
// Time-weighted contribution growth factors (Slice 4).
//
// The annual projection loop grows every bucket once per year. A contribution
// that lands in January gets a full year of growth; one that lands in December
// gets ~one month. Pre-Slice-4 the engine ignored this and treated every
// contribution as if deposited at year start — systematically overstating
// monthly-stream contributions by ~2.7%/yr at a 6% real return, compounding
// to roughly $50-60k of nest-egg overstatement over a 30-year working life.
//
// Approach: closed-form per-rule multiplier applied AT bucket-end, so the
// inner-loop annual structure stays intact. Accuracy is within ~1% of a true
// monthly simulation.
//
// Conventions:
//   - `r` = bucket's annual real return (decimal, e.g. 0.06).
//   - `m` = monthly real rate, derived as `(1 + r)^(1/12) - 1`.
//   - `month` = 1..12 PayMonth. month=1 → full year of growth; month=12 →
//     ~one month of growth.
//   - At `r = 0` every factor reduces to 1 (no growth happens, regardless of
//     when the deposit landed).
//   - The `with_bonus` schedule means "lands in the bonusMonth" — same shape
//     as a `lump_sum` with `month = comp.bonusMonth`.
//   - Defensive fallback: if `with_bonus` is used but `comp.bonusAnnual === 0`
//     the bonus month is meaningless. We treat it as `lump_sum` at month 1
//     (the fully-grown end of the spectrum) so a misconfigured rule errs on
//     the side of MORE growth, never less. Documented choice; a rule expecting
//     to ride a non-existent bonus is itself a user-config bug.
// ────────────────────────────────────────────────────────────────────────────

/** Monthly compounding factor for a year of evenly-spaced monthly deposits.
 *
 *  Math: 12 deposits of `1/12` of the annual amount at the end of each month,
 *  each compounded at the monthly rate `m` for the remaining months. The
 *  closed-form sum is `((1+m)^12 - 1) / m / 12`.
 *
 *  Sanity checks (12 end-of-month deposits, so the December deposit gets
 *  ~zero growth and the January deposit ~11 months — slightly less than the
 *  half-year intuition would suggest):
 *    r = 0.00 → 1.0
 *    r = 0.06 → ~1.0272
 *    r = 0.10 → ~1.0450
 */
export function monthlyGrowthFactor(realReturn: number): number {
  if (realReturn === 0) return 1;
  const m = Math.pow(1 + realReturn, 1 / 12) - 1;
  return (Math.pow(1 + m, 12) - 1) / m / 12;
}

/** Single-deposit growth factor: a lump sum that lands in `month` and earns
 *  `(13 - month) / 12` of a year of growth at the bucket's annual real rate.
 *
 *  Math: factor = (1 + r)^((13 - month) / 12).
 *
 *  Defensive: month is clamped to 1..12 — invalid inputs (e.g. month = 13
 *  through some upstream sign bug) collapse to the December edge so the
 *  contribution still gets ~1 month of growth and never grows beyond a true
 *  year.
 *
 *  Sanity checks at r = 0.06:
 *    month = 1  → 1.0600   (Jan deposit, full 12 months of growth)
 *    month = 6  → ~1.0344  (Jun deposit, ~7 months of growth)
 *    month = 12 → ~1.00487 (Dec deposit, ~1 month of growth)
 */
export function lumpSumGrowthFactor(realReturn: number, month: PayMonth): number {
  if (realReturn === 0) return 1;
  const m = Math.max(1, Math.min(12, month));
  return Math.pow(1 + realReturn, (13 - m) / 12);
}

/** Top-level dispatcher: pick the right growth factor for a `ContributionSchedule`.
 *
 *  - `monthly` → `monthlyGrowthFactor(r)`.
 *  - `with_paycheck` → same as monthly (paychecks land roughly evenly across
 *    the year; bi-weekly vs semi-monthly differences are within engine noise).
 *  - `lump_sum, month=m` → `lumpSumGrowthFactor(r, m)`.
 *  - `with_bonus` → `lumpSumGrowthFactor(r, comp.bonusMonth)`. If the
 *    employer pays no bonus (`bonusAnnual === 0`) the schedule is technically
 *    meaningless, but a rule with `with_bonus` won't have produced any
 *    dollars in that case anyway (it depends on the bonus amount upstream);
 *    we still return a factor so the math is well-defined. Falls back to
 *    month=1 (full-year growth) — see the module-doc note on this choice.
 */
export function scheduleGrowthFactor(
  schedule: ContributionSchedule,
  realReturn: number,
  bonusMonth: PayMonth,
): number {
  switch (schedule.kind) {
    case 'monthly':
    case 'with_paycheck':
      return monthlyGrowthFactor(realReturn);
    case 'lump_sum':
      return lumpSumGrowthFactor(realReturn, schedule.month);
    case 'with_bonus':
      return lumpSumGrowthFactor(realReturn, bonusMonth);
  }
}

/** Resolve a single rule to an annual dollar amount (before cap enforcement).
 *  - fixed_dollar → the stated annual figure
 *  - percent_of_pay (baseOnly: false | undefined) → pct × (base + bonus)
 *  - percent_of_pay (baseOnly: true) → pct × base only
 */
export function resolveRuleAnnual(
  rule: ContributionRule,
  comp: Compensation,
): number {
  if (!rule.enabled) return 0;
  if (rule.mode.kind === 'fixed_dollar') return Math.max(0, rule.mode.annual);
  // percent_of_pay
  const pct = rule.mode.pct;
  if (pct <= 0) return 0;
  if (rule.mode.baseOnly) return comp.baseAnnual * pct;
  return (comp.baseAnnual + comp.bonusAnnual) * pct;
}

/** Compute employer 401(k) match for the year.
 *
 *  Parameterization:
 *    `employer401kMatchPct`    = total % of pay the employer will match (the
 *                                  ceiling on match dollars, expressed as
 *                                  fraction of eligible pay).
 *    `employer401kMatchCapPct` = % of pay the employee must contribute to
 *                                  earn the full match.
 *
 *  Examples:
 *    "100% match up to 6% of pay"  → matchPct = 0.06, capPct = 0.06
 *      (employer matches dollar-for-dollar up to 6% of pay)
 *    "50% match up to 6% of pay"   → matchPct = 0.03, capPct = 0.06
 *      (employer matches 50¢ on each $1 the employee defers, up to 6%)
 *
 *  The formula derives the implicit match-rate-on-employee-contribution
 *  (matchPct / capPct) and applies it to the matchable employee deferral
 *  (capped at capPct × pay). */
export function computeEmployerMatch(
  comp: Compensation,
  employeeRetirementContrib: number,
): number {
  if (!comp.employer401kMatchPct || !comp.employer401kMatchCapPct) return 0;
  if (comp.employer401kMatchCapPct <= 0) return 0;
  const eligiblePay = comp.baseAnnual + comp.bonusAnnual;
  const matchableEmployee = Math.min(
    Math.max(0, employeeRetirementContrib),
    eligiblePay * comp.employer401kMatchCapPct,
  );
  const matchRateOnEmployee =
    comp.employer401kMatchPct / comp.employer401kMatchCapPct;
  return matchableEmployee * matchRateOnEmployee;
}

export interface CapResult {
  /** Per-rule actual contribution (after all caps). Keyed by rule id. */
  byRule: Map<string, number>;
  /** Total employer match (counts against 415(c) but not 402(g)). */
  employerMatch: number;
  /** Per-rule capped-out dollars (intended − actual). For warnings. */
  cappedByRule: Map<string, number>;
  /** Per-rule cap reason (which limit chopped it). For UI / notes. */
  capReasonByRule: Map<string, '402g' | '415c' | 'ira'>;
  /** Sleeve / target sums for downstream routing and reporting. */
  pretaxTotal: number;
  rothTotal: number;
  afterTax401kTotal: number;
  rothIraTotal: number;
  taxableTotal: number;
  hysaTotal: number;
}

/** Apply 402(g), 415(c), and IRA caps to a year's rules in user-defined
 *  priority order. Returns per-rule actual dollars + cap-warning data. */
export function applyElectiveCaps(
  rules: ContributionRule[],
  comp: Compensation,
  age: number,
): CapResult {
  const byRule = new Map<string, number>();
  const cappedByRule = new Map<string, number>();
  const capReasonByRule = new Map<string, '402g' | '415c' | 'ira'>();

  // 1. Resolve intended amounts up front.
  const intended = new Map<string, number>();
  for (const r of rules) intended.set(r.id, resolveRuleAnnual(r, comp));

  // 2. Sort by user-controlled priority (`order`); fall back to id for
  //    determinism on equal orders.
  const ordered = [...rules].sort(
    (a, b) => a.order - b.order || a.id.localeCompare(b.id),
  );

  // 3. 402(g) pass — pretax + roth_401k share one cap.
  let headroom402g = electiveDeferralLimit(age);
  for (const r of ordered) {
    const tt = r.taxTreatment;
    if (tt !== 'pretax' && tt !== 'roth_401k') continue;
    const want = intended.get(r.id) ?? 0;
    const take = Math.max(0, Math.min(want, headroom402g));
    byRule.set(r.id, take);
    headroom402g -= take;
    if (take < want) {
      cappedByRule.set(r.id, want - take);
      capReasonByRule.set(r.id, '402g');
    }
  }

  // 4. Employer match — based on what actually got into 401(k) pretax/Roth.
  const employee401k =
    [...byRule.entries()]
      .filter(([id]) => {
        const r = rules.find(x => x.id === id);
        return r && (r.taxTreatment === 'pretax' || r.taxTreatment === 'roth_401k');
      })
      .reduce((s, [, v]) => s + v, 0);
  const employerMatch = computeEmployerMatch(comp, employee401k);

  // 5. 415(c) pass — total annual additions ≤ limit. Includes employee
  //    deferrals already counted, after-tax, mega-backdoor, AND employer
  //    match. We re-tally to make sure pretax+roth_401k that fit under
  //    402(g) also fit under the (typically larger) 415(c) headroom.
  let headroom415c = annualAdditionsLimit(age) - employerMatch;
  // Subtract already-allocated 402(g) dollars from 415(c) headroom first.
  headroom415c -= employee401k;
  // (If pretax+Roth-401k somehow exceed 415(c) headroom — unusual but
  // possible at very low salaries — we leave the 402(g) allocation alone
  // and let after-tax / mega get nothing. Rationale: the user's stated
  // 401(k) deferral takes priority over after-tax sleeves.)
  for (const r of ordered) {
    const tt = r.taxTreatment;
    if (tt !== 'after_tax_401k' && tt !== 'mega_backdoor_to_roth') continue;
    const want = intended.get(r.id) ?? 0;
    const take = Math.max(0, Math.min(want, headroom415c));
    byRule.set(r.id, take);
    headroom415c -= take;
    if (take < want) {
      cappedByRule.set(r.id, want - take);
      capReasonByRule.set(r.id, '415c');
    }
  }

  // 6. IRA cap pass — direct Roth IRA only (traditional IRA not a separate
  //    treatment in this MVP). A pretax IRA contribution would belong here
  //    too if/when added.
  let headroomIra = iraContributionLimit(age);
  for (const r of ordered) {
    if (r.taxTreatment !== 'roth_ira') continue;
    const want = intended.get(r.id) ?? 0;
    const take = Math.max(0, Math.min(want, headroomIra));
    byRule.set(r.id, take);
    headroomIra -= take;
    if (take < want) {
      cappedByRule.set(r.id, want - take);
      capReasonByRule.set(r.id, 'ira');
    }
  }

  // 7. Uncapped passes — `after_tax_brokerage` and any rule with no
  //    taxTreatment (e.g. a HYSA target). These contribute their full
  //    intended amount.
  for (const r of ordered) {
    if (byRule.has(r.id)) continue;
    byRule.set(r.id, intended.get(r.id) ?? 0);
  }

  // 8. Tally totals by sleeve / target for downstream consumers.
  let pretaxTotal = 0;
  let rothTotal = 0;
  let afterTax401kTotal = 0;
  let rothIraTotal = 0;
  let taxableTotal = 0;
  let hysaTotal = 0;
  for (const r of rules) {
    const v = byRule.get(r.id) ?? 0;
    if (v <= 0) continue;
    switch (r.taxTreatment) {
      case 'pretax':
        pretaxTotal += v;
        break;
      case 'roth_401k':
        rothTotal += v;
        break;
      case 'after_tax_401k':
        afterTax401kTotal += v;
        break;
      case 'mega_backdoor_to_roth':
        rothTotal += v;
        break;
      case 'roth_ira':
        rothIraTotal += v;
        rothTotal += v;
        break;
      case 'after_tax_brokerage':
        taxableTotal += v;
        break;
      default:
        // No taxTreatment: route by target bucket.
        if (r.target.kind === 'bucket') {
          if (r.target.bucket === 'taxable') taxableTotal += v;
          else if (r.target.bucket === 'hysa') hysaTotal += v;
          else if (r.target.bucket === 'retirement') pretaxTotal += v;
        }
    }
  }

  return {
    byRule,
    employerMatch,
    cappedByRule,
    capReasonByRule,
    pretaxTotal,
    rothTotal,
    afterTax401kTotal,
    rothIraTotal,
    taxableTotal,
    hysaTotal,
  };
}
